/**
 * Is there more content past the far edge of this scroller, right now?
 *
 * Two surfaces in the builder clip content and gave no sign of it. The canvas clips day cards below
 * the fold at 1440×900, and the phase band clips tabs to the right on a nine-phase plan. In both
 * cases the scrollbar is the only cue, and on macOS an overlay scrollbar is invisible until the
 * user is already scrolling — so the trainer's evidence that there is more is the act of looking
 * for it.
 *
 * This answers the one question an edge fade needs: not "does this scroll" but "is anything HIDDEN
 * past this edge", which is the question that also turns the fade OFF at the end of the scroll.
 * Sitting under a clipped card the fade says "keep going"; sitting under the last card it would be
 * decoration lying about the same thing.
 *
 * ── Presentation, and it decides nothing ─────────────────────────────────────────────────────
 * It reads layout off the DOM and returns nothing a rule could be written about. `model/` is the
 * pure-decision layer and this measures instead, which is why it is a `lib/` hook.
 *
 * ── One hook, two axes, because the second caller arrived ────────────────────────────────────
 * `TapePane` scrolls in the inline direction and the Ankiety stream in the block one. The
 * measurement is the same three numbers on a different pair of properties, and the observer wiring
 * — the part that is easy to get wrong — is identical.
 *
 * ── Promoted to `shared/` on its second SLICE ────────────────────────────────────────────────
 * It was `pages/plan-builder/lib/`, and the client detail stream needs the same answer for a
 * different reason: a scrolling region is a tab stop only while it has something to scroll, so the
 * stream's `tabIndex` follows this rather than a row count. A page may not import another page's
 * lib, and a copy of an observer is the near-duplicate this repo promotes on sight.
 */

import { useEffect, useRef, useState, type RefObject } from 'react';

/** Which way the scroller runs. `block` is top-to-bottom, `inline` is left-to-right. */
export type OverflowAxis = 'block' | 'inline';

export interface OverflowEdge<T extends HTMLElement> {
  /** Attach to the element that scrolls — the one carrying `overflow: auto`. */
  readonly ref: RefObject<T | null>;
  /**
   * `true` while content is hidden past the far edge. Sub-pixel layouts round, so the threshold is
   * one device-independent pixel rather than zero: a scroller reporting a 0.5px remainder it can
   * never actually scroll to would otherwise pin the fade on for good.
   */
  readonly hasMore: boolean;
  /**
   * `true` while content is hidden past the NEAR edge — above, or to the left.
   *
   * The tape is what made this necessary. The week grid and the phase band both start at their
   * origin and only ever run off the far end, so one answer covered them; a horizontal tape is
   * scrolled INTO, and once the trainer is halfway along it the loop mark ahead and the first
   * training behind are equally hidden. A fade on one side only would say the tape starts here.
   */
  readonly hasPrevious: boolean;
}

export function useOverflowEdge<T extends HTMLElement>(axis: OverflowAxis): OverflowEdge<T> {
  const ref = useRef<T>(null);
  const [hasMore, setHasMore] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (node === null) return;

    const measure = () => {
      const consumed = axis === 'block' ? node.scrollTop : node.scrollLeft;
      const remaining =
        axis === 'block'
          ? node.scrollHeight - node.scrollTop - node.clientHeight
          : node.scrollWidth - node.scrollLeft - node.clientWidth;
      setHasMore(remaining > 1);
      setHasPrevious(consumed > 1);
    };

    measure();
    // `passive`: the handler never calls `preventDefault`, and saying so keeps it off the scroll
    // path's critical work.
    node.addEventListener('scroll', measure, { passive: true });

    // jsdom implements no `ResizeObserver`, and a component test that mounted this would throw on
    // the constructor rather than fail on anything it was testing. The scroll listener still runs
    // there; the observer is what a real browser adds.
    if (typeof ResizeObserver === 'undefined') {
      return () => {
        node.removeEventListener('scroll', measure);
      };
    }

    // The scroller AND its children: resizing the window changes the box, but switching phases
    // changes only what is inside it — a five-card week replacing a three-card one moves the fade
    // with no scroll and no resize of the scroller itself.
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    for (const child of node.children) observer.observe(child);

    return () => {
      node.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [axis]);

  return { ref, hasMore, hasPrevious };
}
